import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { useDealsSearch, useApi } from '@shopista/shared/api/hooks'
import type { DealsSearchResponse } from '@shopista/shared/types'

const TOUCH = '2.75rem'
const HAIRLINE = 'var(--border-width-hairline)'
const wrapStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }
const correctionStyle: CSSProperties = { fontSize: 'var(--font-size-3xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }
const quietStyle: CSSProperties = { border: 'none', background: 'transparent', color: 'var(--color-accent-primary)', fontSize: 'var(--font-size-3xs)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }
const emptyStyle: CSSProperties = { padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-muted)' }
const missLinkStyle: CSSProperties = { ...quietStyle, color: 'var(--color-text-muted)', padding: 'var(--space-2)', textAlign: 'center' }
const missCardStyle: CSSProperties = { border: `${HAIRLINE} solid var(--color-border-subtle)`, borderRadius: 'var(--radius-lg)', padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }
const noteRowStyle: CSSProperties = { display: 'flex', gap: 'var(--space-2)' }
const noteInputStyle: CSSProperties = { flex: 1, minHeight: TOUCH, border: `${HAIRLINE} solid var(--color-border-strong)`, borderRadius: 'var(--radius-md)', padding: '0 var(--space-3)', fontFamily: 'var(--font-family-sans)', fontSize: 'var(--font-size-sm)' }
const submitStyle: CSSProperties = { minHeight: TOUCH, padding: '0 var(--space-4)', border: 'none', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-primary)', color: 'var(--color-text-inverse)', fontWeight: 'var(--font-weight-bold)', cursor: 'pointer' }
const doneStyle: CSSProperties = { fontSize: 'var(--font-size-2sm)', color: 'var(--color-text-secondary)', textAlign: 'center', padding: 'var(--space-2)' }

type MissState = 'idle' | 'logged' | 'thanks'
export interface DealsSearchProps {
  query: string
  exact: boolean
  onUndo: () => void
  /** Retained for test/caller compatibility; filter sections now narrow locally. */
  storeIds?: string[]
  resultCount?: number
  renderResults: (deals: DealsSearchResponse['deals']) => JSX.Element
}

export function DealsSearch({ query: bar, exact, onUndo, resultCount, renderResults }: DealsSearchProps): JSX.Element {
  const api = useApi()
  const [missState, setMissState] = useState<MissState>('idle')
  const [note, setNote] = useState('')
  const searchQuery = useDealsSearch(bar, undefined, exact ? 'off' : undefined)
  const trimmed = bar.trim()
  const deals = searchQuery.data?.deals ?? []
  const visibleCount = resultCount ?? deals.length
  const correction = [...(searchQuery.data?.include_rewrites ?? []), ...(searchQuery.data?.exclude_rewrites ?? [])].find((item) => item.corrected)

  useEffect(() => {
    setMissState('idle')
    setNote('')
  }, [bar])

  const barRef = useRef(bar)
  barRef.current = bar
  const missStateRef = useRef(missState)
  missStateRef.current = missState
  useEffect(() => () => {
    const lastBar = barRef.current.trim()
    if (lastBar && missStateRef.current === 'idle') {
      void api.FlyerDeals.reportAbandon(lastBar).catch(() => {})
    }
  }, [api])

  const handleUndo = useCallback(() => {
    if (!correction) return
    onUndo()
    void api.FlyerDeals.reportRefinement(trimmed, correction.original, false, 'typed').catch(() => {})
  }, [api, correction, onUndo, trimmed])

  const handleMissTap = () => {
    setMissState('logged')
    void api.FlyerDeals.reportMiss(trimmed).catch(() => {})
  }
  const handleNoteSubmit = () => {
    setMissState('thanks')
    const value = note.trim()
    void api.FlyerDeals.reportMiss(trimmed, value || undefined).catch(() => {})
  }

  return <div style={wrapStyle} data-testid="deals-search-surface">
    {correction && <div style={correctionStyle} data-testid="deals-search-correction">
      Showing results for <b>{correction.term}</b> — you typed "{correction.original}"
      <button type="button" style={quietStyle} onClick={handleUndo} data-testid="deals-search-correction-undo">Undo</button>
    </div>}
    {!searchQuery.isLoading && visibleCount === 0 && <div style={emptyStyle} data-testid="deals-search-empty">No deals match "{trimmed}".</div>}
    {deals.length > 0 && renderResults(deals)}
    {missState === 'idle' && <button type="button" style={missLinkStyle} onClick={handleMissTap} data-testid="deals-search-miss-link">Not what you're looking for?</button>}
    {missState === 'logged' && <div style={missCardStyle} data-testid="deals-search-miss-card">
      <span>Thanks — want to tell us more?</span>
      <div style={noteRowStyle}>
        <input style={noteInputStyle} value={note} onChange={(event) => setNote(event.target.value)} placeholder="What were you after, or what's wrong with these?" aria-label="Optional note" data-testid="deals-search-miss-note-input" />
        <button type="button" style={submitStyle} onClick={handleNoteSubmit} data-testid="deals-search-miss-note-submit">Send</button>
      </div>
      <button type="button" style={quietStyle} onClick={handleNoteSubmit} data-testid="deals-search-miss-skip">That's all</button>
    </div>}
    {missState === 'thanks' && <div style={doneStyle} data-testid="deals-search-miss-thanks">Thanks for letting us know.</div>}
  </div>
}
